import React from "react";

import { SITE_URL } from "../lib/siteConfig";
import { HERO_IMAGE, LOGO_IMAGE } from "../lib/siteImages";

/**
 * Injects a JSON-LD structured data block into the page head via portal-less approach.
 * Renders a <script type="application/ld+json"> element inside its own component.
 */
const JsonLd = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, "\\u003c") }}
  />
);

/** Organization + LocalBusiness identity — used on Home & Livraison */
export const OrganizationSchema = () => {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Noctua Riviera",
        alternateName: "Noctua",
        url: SITE_URL,
        logo: LOGO_IMAGE,
        image: HERO_IMAGE,
        description:
          "Livraison d'épicerie 24h/24 de Cannes à Monaco. Restauration de 11h à 4h. Minimum de commande 30 €, frais de livraison fixes de 15 €.",
        telephone: "+33623060308",
        email: "noctua.riviera@gmail.com",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+33623060308",
          contactType: "customer service",
          areaServed: ["FR", "MC"],
          availableLanguage: ["French", "English"],
        },
      },
      {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#business`,
        name: "Noctua Riviera",
        image: HERO_IMAGE,
        url: SITE_URL,
        telephone: "+33623060308",
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Nice",
          postalCode: "06000",
          addressRegion: "Alpes-Maritimes",
          addressCountry: "FR",
        },
        areaServed: [
          { "@type": "City", name: "Cannes" },
          { "@type": "City", name: "Antibes" },
          { "@type": "City", name: "Juan-les-Pins" },
          { "@type": "City", name: "Cagnes-sur-Mer" },
          { "@type": "City", name: "Nice" },
          { "@type": "City", name: "Villefranche-sur-Mer" },
          { "@type": "City", name: "Beaulieu-sur-Mer" },
          { "@type": "City", name: "Saint-Jean-Cap-Ferrat" },
          { "@type": "City", name: "Èze" },
          { "@type": "City", name: "Cap-d'Ail" },
          { "@type": "City", name: "Monaco" },
        ],
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ],
            opens: "00:00",
            closes: "23:59",
          },
        ],
        servesCuisine: ["Italian", "French", "Snacks", "Beverages"],
        potentialAction: {
          "@type": "OrderAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "tel:+33623060308",
            inLanguage: "fr-FR",
            actionPlatform: [
              "http://schema.org/DesktopWebPlatform",
              "http://schema.org/MobileWebPlatform",
            ],
          },
          deliveryMethod: "http://purl.org/goodrelations/v1#DeliveryModeOwnFleet",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "Noctua Riviera",
        inLanguage: "fr-FR",
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  };
  return <JsonLd data={data} />;
};

/** Homepage FAQ — targets common night-delivery Riviera queries */
export const FAQSchema = ({ items }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  return <JsonLd data={data} />;
};

/** Breadcrumb helper for Shop pages */
export const BreadcrumbSchema = ({ trail }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: `${SITE_URL}${t.path}`,
    })),
  };
  return <JsonLd data={data} />;
};

/**
 * Product + Offer list — emits an ItemList schema with each product as a Product node.
 * Describes the listing; this does not guarantee individual product rich results.
 * Used on category pages (/shop/:category).
 */
export const ProductListSchema = ({ categoryName, categoryPath, products }) => {
  if (!products || products.length === 0) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${categoryName} — Noctua Riviera`,
    url: `${SITE_URL}${categoryPath}`,
    numberOfItems: products.length,
    itemListElement: products.slice(0, 30).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        "@id": `${SITE_URL}${categoryPath}#${p.id}`,
        name: p.name,
        description: p.description || `${p.name} — livraison de nuit sur la Côte d'Azur.`,
        image: p.image_url && p.image_url.startsWith("http")
          ? p.image_url
          : p.image_url ? `${SITE_URL}${p.image_url}` : HERO_IMAGE,
        ...(p.brand ? { brand: { "@type": "Brand", name: p.brand } } : {}),
        category: categoryName,
        offers: {
          "@type": "Offer",
          price: Number(p.price).toFixed(2),
          priceCurrency: "EUR",
          ...(typeof p.stock === "number" ? { availability: p.stock > 0 && p.active !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" } : {}),
          url: `${SITE_URL}${categoryPath}`,
          seller: { "@id": `${SITE_URL}/#business` },
        },
      },
    })),
  };
  return <JsonLd data={data} />;
};
